import * as Y from 'yjs'
import type {ListNodePage} from "@/shared/lib/node-registry.ts";

const PAGES_FIELD = 'pages'
const CURRENT_PAGE_INDEX_FIELD = 'currentPageIndex'

// ─── 读写 pages 字段 ────────────────────────────────────────────

/** 从节点 Y.Map 读取 pages 列表 */
export function readPages(yNodeData: Y.Map<unknown>): ListNodePage[] {
    const raw = yNodeData.get(PAGES_FIELD)
    if (raw instanceof Y.Array) return raw.toJSON() as ListNodePage[]
    if (Array.isArray(raw)) return raw as ListNodePage[]
    return []
}

/** 将 pages 列表写回节点 Y.Map */
export function writePages(yNodeData: Y.Map<unknown>, pages: ListNodePage[]): void {
    yNodeData.set(PAGES_FIELD, pages)
}

/** 读取当前页索引 */
export function readCurrentPageIndex(yNodeData: Y.Map<unknown>): number {
    const raw = yNodeData.get(CURRENT_PAGE_INDEX_FIELD)
    return typeof raw === 'number' ? raw : -1
}

/** 设置当前页索引 */
export function setCurrentPageIndex(yNodeData: Y.Map<unknown>, index: number): void {
    yNodeData.set(CURRENT_PAGE_INDEX_FIELD, index)
}

// ─── 页面操作 ───────────────────────────────────────────────────

/** 创建一个新页面 */
export function createListNodePage(params: {
    assetRefs?: string[]
}): ListNodePage {
    return {
        assetRefs: params.assetRefs ?? [],
        failedRefs: [],
    }
}

/** 在 pages 列表头部插入新页 */
export function prependPage(yNodeData: Y.Map<unknown>, page: ListNodePage): void {
    const pages = readPages(yNodeData)
    writePages(yNodeData, [page, ...pages])
}

/** 在 pages 列表末尾追加新页 */
export function appendPage(yNodeData: Y.Map<unknown>, page: ListNodePage): void {
    const pages = readPages(yNodeData)
    writePages(yNodeData, [...pages, page])
}

/** 在指定页的 assetRefs 尾部追加 assetId */
export function appendAssetToPage(
    yNodeData: Y.Map<unknown>,
    pageIndex: number,
    assetId: string,
): void {
    const pages = readPages(yNodeData)
    if (pageIndex < 0 || pageIndex >= pages.length) return
    const updated = [...pages]
    const page = { ...updated[pageIndex] }
    page.assetRefs = [...page.assetRefs, assetId]
    updated[pageIndex] = page
    writePages(yNodeData, updated)
}

/** 从指定页的 assetRefs 中移除一个 assetId */
export function removeAssetFromPage(
    yNodeData: Y.Map<unknown>,
    pageIndex: number,
    assetId: string,
): void {
    const pages = readPages(yNodeData)
    if (pageIndex < 0 || pageIndex >= pages.length) return
    const updated = [...pages]
    const page = { ...updated[pageIndex] }
    const assetIdx = page.assetRefs.indexOf(assetId)
    if (assetIdx !== -1) {
        const newAssetRefs = [...page.assetRefs]
        newAssetRefs.splice(assetIdx, 1)
        page.assetRefs = newAssetRefs
    }
    updated[pageIndex] = page
    writePages(yNodeData, updated)
}

/** 删除指定页，同时返回被删除页的 assetRefs（用于 unlink） */
export function removePage(yNodeData: Y.Map<unknown>, pageIndex: number): ListNodePage | null {
    const pages = readPages(yNodeData)
    if (pageIndex < 0 || pageIndex >= pages.length) return null
    const removed = pages[pageIndex]
    writePages(yNodeData, [...pages.slice(0, pageIndex), ...pages.slice(pageIndex + 1)])
    return removed
}

// ─── 查找 ───────────────────────────────────────────────────────

/** 查找包含指定 assetId 的页面索引 */
export function findPageIndexByAsset(pages: ListNodePage[], assetId: string): number {
    return pages.findIndex((p) => p.assetRefs.includes(assetId))
}

// ─── 页面清理 ───────────────────────────────────────────────────

/** 移除 pages 中所有 assetRefs 为空的页面 */
export function pruneEmptyPages(yNodeData: Y.Map<unknown>): void {
    const pages = readPages(yNodeData)
    const filtered = pages.filter((p) => p.assetRefs.length > 0)
    if (filtered.length !== pages.length) {
        writePages(yNodeData, filtered)
    }
}
